import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Resolve server-returned relative paths (e.g. "/api/uploads/...") to full URLs.
export function resolveUrl(path) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  const base = (BASE || "").replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

// Axios instance — pulls token from sessionStorage on every call
const api = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("sp_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // invalid/expired token — wipe session
      sessionStorage.removeItem("sp_token");
      sessionStorage.removeItem("sp_admin");
    }
    return Promise.reject(err);
  }
);

export function errorMessage(err, fallback = "Something went wrong") {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
}

// ---- Auth ----
export async function login(username, password) {
  const { data } = await api.post("/auth/login", { username, password });
  sessionStorage.setItem("sp_token", data.token);
  sessionStorage.setItem("sp_admin", "1");
  return data;
}

export function logout() {
  sessionStorage.removeItem("sp_token");
  sessionStorage.removeItem("sp_admin");
}

export async function verifyAdmin() {
  const { data } = await api.get("/auth/me");
  return data;
}

// ---- Photos ----
export async function listPhotos() {
  const { data } = await api.get("/photos");
  return (data || []).map((p) => ({ ...p, src: resolveUrl(p.src) }));
}
export async function uploadPhoto({ file, caption, category }) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("caption", caption || "");
  fd.append("category", category || "Recent");
  const { data } = await api.post("/photos", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { ...data, src: resolveUrl(data.src) };
}
export async function deletePhoto(id) {
  const { data } = await api.delete(`/photos/${id}`);
  return data;
}

// ---- Blogs ----
export async function listBlogs() {
  const { data } = await api.get("/blogs");
  return data;
}
export async function createBlog(blog) {
  const { data } = await api.post("/blogs", blog);
  return data;
}
export async function deleteBlog(id) {
  const { data } = await api.delete(`/blogs/${id}`);
  return data;
}

// ---- Events ----
export async function listEvents() {
  const { data } = await api.get("/events");
  return data;
}
export async function createEvent(ev) {
  const { data } = await api.post("/events", ev);
  return data;
}
export async function deleteEvent(id) {
  const { data } = await api.delete(`/events/${id}`);
  return data;
}

// ---- Messages ----
export async function submitMessage(msg) {
  const { data } = await api.post("/messages", msg);
  return data;
}
export async function listMessages() {
  const { data } = await api.get("/messages");
  return data;
}
export async function deleteMessage(id) {
  const { data } = await api.delete(`/messages/${id}`);
  return data;
}

// ---- Photo Pins (memories on archival photos) ----
export async function listPins(photoKey) {
  const { data } = await api.get("/pins", { params: { photo_key: photoKey } });
  return data;
}
export async function createPin(pin) {
  const { data } = await api.post("/pins", pin);
  return data;
}
export async function deletePin(id) {
  const { data } = await api.delete(`/pins/${id}`);
  return data;
}

export default api;
